import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import { Icon, Button, TextField, Panel, PageHead, PageLoading, StatCell } from "@/sentinel";

/** Thousands separators and a true minus, as the original toLocaleString figures but signed correctly. */
const amt = (v) => {
  const n = Number(v) || 0;
  return (n < 0 ? "−" : "") + Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 2 });
};

export default function Budget() {
  const { can } = useAuth();
  const canEdit = can("analyst");
  const qc = useQueryClient();
  const { data: b, refetch } = useQuery({ queryKey: ["pm-budget"], queryFn: api.pmBudget });
  const { data: settings } = useQuery({ queryKey: ["pm-settings"], queryFn: api.pmSettings });
  const [budgetEdit, setBudgetEdit] = useState("");

  useEffect(() => { if (settings) setBudgetEdit(String(settings.budget || 0)); }, [settings?.budget]);

  const saveBudget = async () => {
    try {
      await api.pmUpdateSettings({ labour_rate: settings.labour_rate, currency: settings.currency, budget: Number(budgetEdit) });
      qc.invalidateQueries({ queryKey: ["pm-settings"] }); refetch();
      toast.success("Budget updated.");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };

  if (!b) return <PageLoading label="Loading budget" />;
  const cur = b.currency;
  const pct = b.budget > 0 ? Math.min(100, (b.actual_to_date / b.budget) * 100) : 0;
  const over = b.budget > 0 && b.actual_to_date > b.budget;
  // Composition of spend to date on the sequential ramp: labour, then one-time costs.
  const labourPct = b.budget > 0 ? Math.min(100, (b.labour_cost / b.budget) * 100) : 0;
  const onetimePct = Math.max(0, pct - labourPct);

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="pm-budget">
      <PageHead
        crumb={["Project", "Budget"]}
        title="Budget vs actual"
        context={<>Labour (<span className="sn-num">{b.total_hours} h @ {cur} {b.rate}/h</span>) plus one-time costs against a project budget. Recurring costs are shown separately.</>}
      />

      <Panel title="Project budget" icon="Wallet">
        <div className="sn-row" style={{ alignItems: "flex-end" }}>
          <TextField label={`Project budget (${cur})`} type="number" step="100" numeric value={budgetEdit} onChange={(e) => setBudgetEdit(e.target.value)}
            disabled={!canEdit} style={{ width: 144 }} data-testid="pm-budget-input" />
          {canEdit && <Button variant="primary" onClick={saveBudget} data-testid="pm-budget-save">Save budget</Button>}
        </div>
      </Panel>

      <Panel title="Summary" icon="DollarSign" meta={<span className="sn-num">{cur}</span>}>
        <div className="sn-grid-12">
          <div className="sn-col-4"><StatCell size="md" label="Budget" value={amt(b.budget)} unit={cur} /></div>
          <div className="sn-col-4"><StatCell size="md" label="Actual to date" value={amt(b.actual_to_date)} unit={cur} /></div>
          <div className="sn-col-4"><StatCell size="md" label="Remaining" value={amt(b.remaining)} unit={cur} sub={b.remaining < 0 ? "Over budget" : undefined} /></div>
          <div className="sn-col-4"><StatCell size="md" label="Labour" value={amt(b.labour_cost)} unit={cur} /></div>
          <div className="sn-col-4"><StatCell size="md" label="One-time costs" value={amt(b.onetime_cost)} unit={cur} /></div>
          <div className="sn-col-4"><StatCell size="md" label="Monthly recurring" value={amt(b.monthly_recurring)} unit={cur} sub="Shown separately" /></div>
        </div>
      </Panel>

      <Panel title="Spend vs budget" icon="Gauge"
        meta={<span className="sn-num">{pct.toFixed(0)}%{over ? " · OVER BUDGET" : ""}</span>}>
        <div className="sn-stack" style={{ gap: "var(--space-2)" }}>
          <div role="img" aria-label={`Spend is ${pct.toFixed(0)}% of budget${over ? ", over budget" : ""}`}
            style={{ position: "relative", height: 12, borderRadius: "var(--radius-xs)", background: "var(--border-hairline)", overflow: "hidden" }}>
            <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${labourPct}%`, background: "var(--viz-seq-4)" }} />
            <span style={{ position: "absolute", left: `${labourPct}%`, top: 0, bottom: 0, width: `${onetimePct}%`, background: "var(--viz-seq-2)" }} />
          </div>
          <div className="sn-row sn-small sn-muted" style={{ gap: "var(--space-4)" }}>
            <span className="sn-row" style={{ gap: "var(--space-1)" }}><span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 2, background: "var(--viz-seq-4)" }} />Labour</span>
            <span className="sn-row" style={{ gap: "var(--space-1)" }}><span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 2, background: "var(--viz-seq-2)" }} />One-time costs</span>
            {over && <span className="sn-row" style={{ gap: "var(--space-1)", color: "var(--text-primary)" }}><Icon name="TriangleAlert" size={14} />Actual spend exceeds the budget.</span>}
          </div>
        </div>
      </Panel>
    </div>
  );
}
